/**
 * Logger utility for development-only logging
 * In production, logs are suppressed to improve performance and security
 */

const isDevelopment = process.env.NODE_ENV === 'development';

class Logger {
  /**
   * Log general information (only in development)
   */
  log(...args: any[]): void {
    if (isDevelopment) {
      console.log(...args);
    }
  }

  /**
   * Log informational messages (only in development)
   */
  info(...args: any[]): void {
    if (isDevelopment) {
      console.info(...args);
    }
  }

  /**
   * Log warnings (only in development)
   */
  warn(...args: any[]): void {
    if (isDevelopment) {
      console.warn(...args);
    }
  }

  /**
   * Log errors (sanitized in production)
   * In production, sensitive details are omitted
   */
  error(...args: any[]): void {
    if (isDevelopment) {
      console.error(...args);
    } else {
      // In production, log generic error without sensitive details
      console.error('An error occurred. Please contact support if this persists.');
    }
  }

  /**
   * Log debug information (only in development)
   */
  debug(...args: any[]): void {
    if (isDevelopment) {
      console.debug(...args);
    }
  }

  /**
   * Always log critical errors, even in production
   * Use sparingly for truly critical system errors
   */
  critical(message: string, error?: Error): void {
    console.error(`[CRITICAL] ${message}`);
    if (error && isDevelopment) {
      console.error(error);
    }
  }
}

export const logger = new Logger();

// Export individual functions for convenience
export const { log, info, warn, error, debug, critical } = logger;

export default logger;
